import _ from 'lodash'
import { OrderDetails, Order, IOrderProductsDict, Product, IProduct } from '@/service/types';
import { 
  IOrderState,
  IValidationWrappedProductDict,
  ValidationStatus,
  IProductValidationDict,
  IValidationWrappedProduct,
  IProductValidation,
  IGeneralMessagesDict,
  MessageType,
  IValidationProcess,
  IProductDict
} from '@/store/types';
import i18n from '@/i18n';

class ValidationProcessBuilder {
  private dict: IValidationWrappedProductDict = {};
  private keys: Array<string> = [];
  private products: IOrderProductsDict = {};
  private validations: IProductValidationDict = {};
  private generalMessages: IGeneralMessagesDict = {};
  private valid: boolean = true;
  private unconfirmedCounter = 0;
  private pristine: boolean = true;

  constructor(
    products: IOrderProductsDict,
    validations: IProductValidationDict
  ) {
    this.products = products;
    this.validations = validations;
  }

  build(): IValidationProcess {
    this.collectAllKeys();
    this.fill();
    this.endThoughts();
    return {
      products: this.dict,
      generalMessages: this.generalMessages,
      valid: this.valid,
      pristine: this.pristine
    };
  }

  private collectAllKeys() {
    this.keys = _.union(_.keys(this.products), _.keys(this.validations));
  }

  private fill() {
    _.each(this.keys, (key) => {
      const product = _.get(this.products, key, null);
      let validation = _.get(this.validations, key, null);
      const state = this.establishState(product, validation);
      this.updateGeneralValidationStatus(state);
      this.dict[key] = this.buildDictItem(product, validation, state);
    });
  }

  private establishState(
    product: Product|null, 
    validation: IProductValidation|null
  ): ValidationStatus {
    
    if (!product && validation) {
      return ValidationStatus.Extraneous;
    } else if (product && validation && validation.quantity) {
      if (product.quantity > validation.quantity) {
        return ValidationStatus.TooFew
      } else if (product.quantity < validation.quantity) {
        return ValidationStatus.TooMuch;
      } else {
        return ValidationStatus.Ok;
      }
    } else if (product && !validation) {
      return ValidationStatus.TooFew;
    } else {
      return ValidationStatus.OtherError;
    }
  }

  private updateGeneralValidationStatus(state: ValidationStatus) {
    if (ValidationStatus.Extraneous === state && !_.has(this.generalMessages, 'extraneous')) {
      this.generalMessages.extraneous = {
        type: MessageType.Danger,
        value: _.toString(i18n.t('dashboard.orderDetails.ValidationStatus.extraneous'))
      };
      this.valid = false;
    }

    if (_.indexOf([
      ValidationStatus.Ok,
      ValidationStatus.Extraneous
    ], state) === -1) {
      this.unconfirmedCounter++;
      this.generalMessages.nbrOfUnconfirmed = {
        type: MessageType.Danger,
        value: _.toString(i18n.t('dashboard.orderDetails.ValidationStatus.nbrOfUnconfirmed', [ this.unconfirmedCounter ]))
      };
      this.valid = false;
    }
  }

  private endThoughts() {
    if (this.valid) {
      this.generalMessages.ok = {
        type: MessageType.Success,
        value: _.toString(i18n.t('dashboard.orderDetails.ValidationStatus.ok'))
      };
    }
  }

  private buildDictItem(
    product: Product|null,
    validation: IProductValidation|null,
    state: ValidationStatus
  ): IValidationWrappedProduct {
    const wrapped: IValidationWrappedProduct = {
      state
    };
    if (product) {
      wrapped.product = product;
    }
    if (validation) {
      wrapped.validation = validation;
      if (validation.product) {
        wrapped.product = validation.product;
      }
    }
    return wrapped;
  }
}

export default {
  all(state: IOrderState, getters: any) {
    let queueIds = _.map(getters.getQueue, (queueEl) => {
      return queueEl.idOrder;
    });
    return _.filter(_.get(state, 'orders'), (item) => {
      return _.indexOf(queueIds, item.id) === -1;
    });
  },
  one(state: IOrderState, getters: any) {
    return (orderId: number): OrderDetails|null => {
      return _.get(state, `ordersDetailed.${orderId}`, null);
    };
  },
  oneComposed(state: IOrderState, getters: any) {
    return (orderId: number): OrderDetails|null => {
      let order = _.cloneDeep(_.find(state.orders, (item) => {
        return item.id == orderId;
      }));
      if (!order) {
        return null;
      }
      let products = getters.orderProducts(orderId);
      let composed = {
        ...order,
        products
      };
      return new OrderDetails(composed);
    };
  },
  getIdsFromMap(state: IOrderState, getters: any, rootState: any, rootGetters: any) {
    const allIds: any = {};
    _.each(state.orderProductsMap, (encoded) => {
      let products = _.split(encoded, ',');
      if (!products.length) {
        return true;
      }
      _.each(products, (product) => {
        let parts = _.split(product, ':');
        let idProduct = _.get(parts, 0);
        allIds[idProduct] = true;
      });
    });
    return Object.keys(allIds).map(v => parseInt(v));
  },
  orderProducts(state: IOrderState, getters: any, rootState: any, rootGetters: any) {
    return (orderId: number) => {
      let sOrderId = _.toString(orderId);
      let encoded = _.get(state.orderProductsMap, sOrderId, '');
      let products = _.split(encoded, ',');
      let output: IProductDict = {};
      if (!products.length) {
        return output;
      }
      _.each(products, (product) => {
        let parts = _.split(product, ':');
        let idProduct = _.get(parts, 0);
        let qty = parseInt(_.get(parts, 1));
        let productObj: IProduct = _.cloneDeep(rootGetters['product/get'](idProduct));
        if (productObj) {
          productObj.quantity = qty;
          output[idProduct] = productObj;
        }
      });
      return output;
    }
  },
  searchQuery(state: IOrderState, getters: any) {
    return _.get(state, 'searchQuery');
  },
  validationProcess(state: IOrderState, getters: any) {
    return (orderId: number): IValidationProcess => {
      const products: IOrderProductsDict = _.get(getters.oneComposed(orderId), `products`, {});
      const validations: IProductValidationDict = getters.validations(orderId);
      return (new ValidationProcessBuilder(products, validations)).build();
    };
  },
  validations(state: IOrderState) {
    return (orderId: number): IProductValidationDict => {
      let items: IProductValidationDict = _.cloneDeep(_.get(state, `ordersValidationContext.${orderId}.productValidation`, {}));
      _.each(items, (item, k) => {
        if (!item.quantity) {
          delete items[k];
        }
      });
      return items;
    }
  },
  getQueue(state: IOrderState) {
    return _.get(state, 'queue', []);
  },
  nextPossibleUpdateTime(state: IOrderState) {
    return state.nextPossibleUpdateTime;
  },
};